import { useEffect, useRef, useState } from "react";
import type {
  Control,
  FieldPath,
  FieldValues,
  Path,
  PathValue,
} from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";

interface UseReverseGeocodeOptions<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  latName: FieldPath<TFieldValues>;
  lonName: FieldPath<TFieldValues>;
  addressName?: FieldPath<TFieldValues>;
  enabled?: boolean;
}

interface ReverseGeocodeResult {
  isGeocoding: boolean;
}

/**
 * Hook to handle reverse geocoding when lat/lon changes
 * Automatically updates the address field if provided
 */
export function useReverseGeocode<TFieldValues extends FieldValues>({
  control,
  latName,
  lonName,
  addressName,
  enabled = true,
}: UseReverseGeocodeOptions<TFieldValues>): ReverseGeocodeResult {
  const { setValue } = useFormContext<TFieldValues>();
  const lat = useWatch({ control, name: latName });
  const lon = useWatch({ control, name: lonName });
  const addressValue = useWatch({
    control,
    name: addressName || ("address" as Path<TFieldValues>),
  });

  const [isGeocoding, setIsGeocoding] = useState(false);
  const isGeocodingRef = useRef(false);
  const lastGeocodedLatLonRef = useRef<{ lat: number; lon: number } | null>(
    null,
  );
  const initialAddressRef = useRef<string | undefined>(addressValue);

  // Store initial address value on mount
  useEffect(() => {
    if (
      addressName &&
      initialAddressRef.current === undefined &&
      addressValue
    ) {
      initialAddressRef.current = addressValue;
    }
  }, [addressValue, addressName]);

  // Reverse geocode when lat/lon changes
  useEffect(() => {
    if (!enabled || !addressName) return;

    // Only geocode if lat/lon are valid numbers
    if (
      typeof lat === "number" &&
      typeof lon === "number" &&
      !Number.isNaN(lat) &&
      !Number.isNaN(lon) &&
      !isGeocodingRef.current
    ) {
      // Skip if this lat/lon was already geocoded
      const lastGeocoded = lastGeocodedLatLonRef.current;
      if (
        lastGeocoded &&
        Math.abs(lastGeocoded.lat - lat) < 0.0001 &&
        Math.abs(lastGeocoded.lon - lon) < 0.0001
      ) {
        return;
      }

      // Only auto-fill if address is empty or matches initial value (not manually edited)
      const shouldGeocode =
        !addressValue ||
        addressValue.trim() === "" ||
        addressValue === initialAddressRef.current;

      if (!shouldGeocode) {
        return;
      }

      isGeocodingRef.current = true;
      setIsGeocoding(true);

      fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1&accept-language=fa`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (data?.address) {
            // Craft address string from address object
            const addressParts: string[] = [];

            if (data.address.state) {
              const state = data.address.state
                .replace(/\s*(استان|محافظة|province)\s*/gi, " ")
                .trim();
              if (state) addressParts.push(state);
            }

            if (data.address.city) {
              addressParts.push(data.address.city);
            }

            if (data.address.neighbourhood) {
              addressParts.push(data.address.neighbourhood);
            }

            if (data.address.road) {
              addressParts.push(data.address.road);
            }

            const formattedAddress = addressParts.join("، ");

            if (formattedAddress && addressName) {
              // Update address field
              setValue(
                addressName as Path<TFieldValues>,
                formattedAddress as PathValue<TFieldValues, Path<TFieldValues>>,
                {
                  shouldValidate: false,
                  shouldDirty: true,
                },
              );
              lastGeocodedLatLonRef.current = { lat, lon };
              // Update initial address ref to the geocoded value
              initialAddressRef.current = formattedAddress;
            }
          }
        })
        .catch((error) => {
          console.error("Error reverse geocoding:", error);
        })
        .finally(() => {
          isGeocodingRef.current = false;
          setIsGeocoding(false);
        });
    }
  }, [lat, lon, addressValue, addressName, enabled, setValue]);

  return { isGeocoding };
}
